<template>
  <v-container>
    <v-card
      class="mx-auto"
      max-width="1000"
    >
      <v-card-title>
        <span class="text-h6">Inventory Source Settings</span>
      </v-card-title>
      <v-card-text>
        <v-table>
          <thead>
            <tr>
              <th rowspan="2"><b>Environment</b></th>

              <th
                :colspan="configStore.inventorySources.length + 1"
                class="text-center"
              >
                <b> Inventory Sources</b>
              </th>
            </tr>
            <tr>
              <th
                v-for="src in configStore.inventorySources"
                :key="src.id"
              >
                {{ src.aliasName }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="env in configStore.environments"
              :key="env.id"
            >
              <td>
                <v-chip
                  color="primary"
                  text-color="white"
                  class="ma-1"
                  >{{ env.aliasName }}</v-chip
                >
              </td>
              <template v-if="settings && settings[env.name]">
                <td
                  v-for="src in configStore.inventorySources"
                  :key="src"
                >
                  <v-switch
                    v-model="settings[env.name][src.name]"
                    color="success"
                    hide-details
                    density="compact"
                    inset
                  />
                </td>
              </template>
            </tr>
          </tbody>
        </v-table>
        <v-divider class="my-4" />

        <v-row justify="end">
          <v-col cols="auto">
            <v-btn
              color="primary"
              @click="dialog = true"
              class="mt-2"
            >
              Save Settings
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Save Confirmation Modal -->
    <v-dialog
      v-model="dialog"
      max-width="500"
    >
      <v-card>
        <v-card-title>
          <span class="text-h6">Confirm Save</span>
        </v-card-title>
        <v-card-text> Are you sure you want to save these inventory source settings? </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn @click="dialog = false">Cancel</v-btn>
          <v-btn
            color="primary"
            @click="saveSettings"
            >Confirm</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
  <!-- Alert Message -->
  <v-snackbar
    v-model="isShowSnackBar"
    multi-line
    :globalTop="true"
    :color="alertType"
    dismissible
    class="mb-4"
    @click:close="clearAlert"
  >
    {{ alertMessage }}
  </v-snackbar>
</template>

<script setup lang="ts">
import { useAlert } from '@/composables/useAlert'
import { onMounted, reactive, ref } from 'vue'
const configStore = useConfigStore()

const { alertMessage, alertType, isShowSnackBar, showAlert, clearAlert } = useAlert()

// Helper to map API names to your constants

const dialog = ref(false)
const settings = reactive<Record<string, Record<string, boolean>>>({})
// Store mapping for PUT requests
const recordMap = reactive<
  Record<
    string,
    | {
        id: number
        environmentId: number
        inventorySourceId: number
      }
    | undefined
  >
>({})

// Update fetchOptions to fill recordMap
async function fetchOptions() {
  try {
    const response = await $fetch(`/api/setting/environment-inventory-sources`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    })
    const success = (response as any).success
    const data = (response as any).data
    if (!success) {
      console.error('Failed to fetch options:', data)
      return
    }

    // Fill from API response
    for (const rec of data) {
      settings[rec.environment] = settings[rec.environment] || {}
      settings[rec.environment][rec.inventorySource] = rec.enabled
      recordMap[`${rec.environment}_${rec.inventorySource}`] = {
        id: rec.id,
        environmentId: rec.environmentId,
        inventorySourceId: rec.inventorySourceId,
      }
    }
  } catch (err: any) {
    console.error('Error fetching data:', err)
  }
}

// Save all settings via PUT requests
async function saveSettings() {
  dialog.value = false
  const updates = []
  for (const env of configStore.environments) {
    for (const src of configStore.inventorySources) {
      const key = `${env.name}_${src.name}`
      const record = recordMap[key]
      if (record) {
        updates.push({
          id: record.id,
          environmentId: record.environmentId,
          inventorySourceId: record.inventorySourceId,
          enabled: settings[env.name][src.name],
        })
      }
    }
  }
  try {
    const response = await $fetch('/api/setting/environment-inventory-sources', {
      method: 'PUT',
      body: updates,
      headers: { 'Content-Type': 'application/json' },
    })
    if (!response.success) {
      throw new Error((response as any).message || 'Failed to save settings')
    }
    await fetchOptions()
    showAlert((response as any).message || 'Settings saved successfully', 'success')
  } catch (err: any) {
    showAlert(err.message || 'Failed to save settings', 'error')
  } finally {
    setTimeout(() => {
      clearAlert()
    }, 3000)
  }
}
onMounted(async () => {
  await fetchOptions()
})
</script>
